// Entitlement state for the client. Mirrors useMe's module cache + pub/sub,
// plus persistence in Capacitor Preferences so an offline premium user isn't
// locked out of paid features (honored until expires_at + 72h, matching the
// server's renewal slack).
import { useEffect, useState } from "react";
import { api } from "./api";
import { isNativeApp } from "./native";
import type { Entitlement } from "@shared/types";

const STORE_KEY = "caloly.entitlement";
const OFFLINE_SLACK_SEC = 72 * 3600;

let cache: Entitlement | null = null;
let inflight: Promise<Entitlement> | null = null;
const listeners = new Set<() => void>();

async function persist(e: Entitlement): Promise<void> {
  try {
    const { Preferences } = await import("@capacitor/preferences");
    await Preferences.set({ key: STORE_KEY, value: JSON.stringify(e) });
  } catch { /* web without plugin — localStorage fallback */
    try { localStorage.setItem(STORE_KEY, JSON.stringify(e)); } catch { /* ignore */ }
  }
}

async function loadPersisted(): Promise<Entitlement | null> {
  let raw: string | null = null;
  try {
    const { Preferences } = await import("@capacitor/preferences");
    raw = (await Preferences.get({ key: STORE_KEY })).value;
  } catch {
    try { raw = localStorage.getItem(STORE_KEY); } catch { /* ignore */ }
  }
  if (!raw) return null;
  try {
    const e = JSON.parse(raw) as Entitlement;
    // Expired cached premium (past the slack) degrades to free offline.
    if (e.tier === "premium" && e.expires_at !== null &&
        e.expires_at + OFFLINE_SLACK_SEC < Date.now() / 1000) {
      return { tier: "free", source: null, expires_at: null, will_renew: false };
    }
    return e;
  } catch { return null; }
}

async function fetchEntitlement(): Promise<Entitlement> {
  try {
    const e = await api.entitlement();
    cache = e;
    void persist(e);
    return e;
  } catch {
    // Offline: honor the persisted state rather than downgrading.
    const persisted = await loadPersisted();
    cache = persisted ?? { tier: "free", source: null, expires_at: null, will_renew: false };
    return cache;
  }
}

export function useEntitlement(): { entitlement: Entitlement | null; premium: boolean; billingEnabled: boolean; refresh: () => void } {
  const [ent, setEnt] = useState<Entitlement | null>(cache);
  const [tick, setTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    if (cache) { setEnt(cache); return; }
    inflight ??= fetchEntitlement();
    inflight.then((e) => { if (!cancelled) setEnt(e); }).catch(() => {});
    return () => { cancelled = true; };
  }, [tick]);

  useEffect(() => {
    const onInvalidate = () => setTick((t) => t + 1);
    listeners.add(onInvalidate);
    return () => { listeners.delete(onInvalidate); };
  }, []);

  return {
    entitlement: ent,
    premium: ent?.tier === "premium",
    // Native buys through StoreKit; the web buys through Stripe. Each platform
    // listens to its own flag, so an Apple-only configuration no longer makes
    // the website advertise a checkout that cannot succeed.
    billingEnabled: isNativeApp()
      ? ent?.billing_enabled !== false
      : ent?.billing_web_enabled === true,
    refresh: invalidateEntitlement,
  };
}

/** Clear + refetch everywhere (call after purchase/restore/logout). */
export function invalidateEntitlement(): void {
  cache = null;
  inflight = null;
  for (const l of listeners) l();
}

/**
 * Poll the server after a purchase until RevenueCat's webhook lands (≤30s).
 * The store confirms instantly but our row is written by the webhook, so
 * without this the user would pay and still look free until a restart.
 * Returns whatever the server last said — the caller decides how to phrase a
 * timeout, and premium arrives on its own once the webhook is processed.
 */
export async function pollEntitlement(timeoutMs = 30_000): Promise<Entitlement> {
  const start = Date.now();
  for (;;) {
    invalidateEntitlement();
    const e = await fetchEntitlement();
    if (e.tier === "premium" || Date.now() - start > timeoutMs) return e;
    await new Promise((r) => setTimeout(r, 2500));
  }
}

